import { useId } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import { priceFormatter } from "@/src/helpers/number-formatter";
import { createImageUrl } from "@/src/helpers/create-image-url";
import { setCookie } from "helpers/cookie";

import LocationIcon from "assets/svg/location_icon.svg";

import { LazyImage } from "src/components/common/lazy-image/LazyImage";
import BadgeCard from "components/common/badge-card";
import AddFavoriteButton from "components/common/add-favorite-button";
import ShowPhone from "components/common/show-phone/ShowPhone";

import styles from "./styles.module.css";

const HighlighterListingCard = ({ quickInfo, meterPrice, meterArea, badges, listItemInfo, gtmEventLabel, index }) => {
  const keyId = useId();
  const {
    categoryTypeName: category,
    locationSummary,
    priceDetail: {
      price,
      currency: priceCurrency,
      alternativeValue,
      previousPrice,
      previousCurrency: previousPriceCurrency,
      trendType,
    },
    title,
    images,
    phoneNumber,
    url: href,
    estateTypeName: estateType,
    location,
  } = listItemInfo || {};

  const cityName = location?.city?.name;
  const districtName = location?.district?.name;
  const townName = location?.town?.name;
  const localityName = location?.locality?.name;
  const isFirstDisplayedListingCard = [0, 1, 2, 3].includes(index);

  const imageUrl = createImageUrl(images[0], {
    width: 530,
    height: 320,
  });

  const titleClass = classNames({
    [styles.title]: true,
    [styles.hiddenOverflow]: true,
  });

  const quickinfoClass = classNames({
    [styles.quickinfo]: true,
    [styles.hiddenOverflow]: true,
  });

  const locationWrapperClass = classNames({
    [styles.locationWrapper]: true,
    [styles.hiddenOverflow]: true,
  });

  const phoneWrapper = classNames(styles.hiddenOverflow, styles.phoneWrapper);

  const handleClick = () => {
    localStorage.setItem("last_view", category);
  };

  const renderBadges = () => {
    if (!badges || badges === 0) return null;
    let badgeList = [...badges];
    const isOpportunity = badges.find((badge) => badge === "opportunity");
    if (isOpportunity && badges.length > 2) {
      badgeList = badges.filter((badge) => badge === "promotion");
      badgeList.push("opportunity");
    }
    return badgeList.map((badgeType, index) => <BadgeCard key={`${keyId}-${index}`} type={badgeType} />);
  };

  return (
    <div className="w-full">
      <a className={styles.wrapper} href={href} onClick={handleClick}>
        <div className={styles.badges}>{renderBadges()}</div>
        <div className={styles.favoriteIcon}>
          <AddFavoriteButton
            cityName={cityName}
            districtName={districtName}
            townName={townName}
            localityName={localityName}
            listingId={listItemInfo.id}
          />
        </div>
        <div className={styles.imageWrapper}>
          <LazyImage
            src={imageUrl}
            alt="showcase_listing_image"
            className={styles.imageClass}
            visibleByDefault={isFirstDisplayedListingCard}
          />
        </div>
        <div className={styles.contentWrapper}>
          <div className={titleClass}>{title}</div>
          <div className={quickinfoClass}>
            {category === "Arsa" && estateType && meterArea
              ? `${estateType} | ${meterArea}`
              : quickInfo?.map((info) => info?.value).join(" | ")}
          </div>
          <div className={locationWrapperClass}>
            <LocationIcon /> <span className={styles.location}>{locationSummary}</span>
          </div>
          <span className={styles.hiddenOverflow}>
            {price ? (
              <span className={styles.price}>{`${priceFormatter(price)} ${priceCurrency}`}</span>
            ) : (
              <span className={styles.price}>{alternativeValue}</span>
            )}
            {previousPrice && trendType !== "up" && (
              <span className={styles.previousPrice}>
                {`${priceFormatter(previousPrice)} ${previousPriceCurrency}`}
              </span>
            )}
          </span>
          {category === "Arsa" && meterPrice && <div className={styles.meterPrice}> {meterPrice}</div>}
          <div className={phoneWrapper}>
            <ShowPhone
              isTextView
              phoneNumber={phoneNumber}
              listItemInfo={listItemInfo}
              gtmEventLabel={`anasayfa||${gtmEventLabel}`}
            />
          </div>
        </div>
      </a>
    </div>
  );
};

HighlighterListingCard.propTypes = {
  quickInfo: PropTypes.array,
  previousPrice: PropTypes.string,
  previousPriceCurrency: PropTypes.string,
  meterPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  meterArea: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  estateType: PropTypes.string,
  badges: PropTypes.array,
  listItemInfo: PropTypes.object,
  gtmEventLabel: PropTypes.string,
  index: PropTypes.number,
};

export default HighlighterListingCard;
